/**
 * RecoveredRevenueWidget — dashboard card showing campaign-attributed
 * recovery (Iter 138, Feb 2026).
 *
 * Pulls `/api/campaigns/recovered-summary?days=30` and renders the headline
 * recovery number (₹X this month) plus a small "top campaigns" strip. Hidden
 * for non-admin/leader/super_admin so salespeople don't see ROI numbers.
 */

import { useEffect, useState } from "react";
import { campaignsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Megaphone, TrendingUp, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function RecoveredRevenueWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    campaignsApi.recoveredSummary(30)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Visibility — managers only. Salespeople see different metrics.
  if (!["admin", "leader", "super_admin"].includes(user?.role)) return null;

  // Hide entirely until we have at least one send. Avoids cluttering the
  // dashboard on day 1 / preview environments where no campaigns have run.
  if (!loading && (!data || data.sent === 0)) return null;

  const isSuper = user?.role === "super_admin";

  return (
    <div
      className="rounded-2xl p-5 border border-emerald-100 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)" }}
      data-testid="recovered-revenue-widget"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-white/80 rounded-lg shadow-sm">
            <Megaphone size={16} className="text-emerald-700" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
              Recovered via campaigns
            </p>
            <p className="text-[10px] text-emerald-600/80">Last 30 days</p>
          </div>
        </div>
        {isSuper && (
          <Link
            to="/campaigns"
            className="text-[10px] font-bold text-emerald-700 hover:underline"
            data-testid="recovered-widget-link"
          >
            Manage →
          </Link>
        )}
      </div>

      {loading ? (
        <div className="mt-4 flex items-center justify-center h-16">
          <Loader2 size={16} className="animate-spin text-emerald-600" />
        </div>
      ) : (
        <>
          <div className="mt-3">
            <div
              className="text-3xl font-extrabold text-emerald-800"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="recovered-revenue-amount"
            >
              ₹{Math.round(data.revenue_recovered_inr || 0).toLocaleString("en-IN")}
            </div>
            <div className="flex items-center gap-2 mt-1 text-[11px]">
              <span className="font-bold text-emerald-700" data-testid="recovered-orders-count">
                {data.orders_recovered} order{data.orders_recovered === 1 ? "" : "s"}
              </span>
              <span className="text-emerald-600/70">·</span>
              <span className="text-emerald-700">
                {data.sent} sent · {data.replied} replied
              </span>
              <span className="text-emerald-600/70">·</span>
              <span className="inline-flex items-center gap-0.5 font-bold text-emerald-700">
                <TrendingUp size={11} /> {data.conversion_rate}% CVR
              </span>
            </div>
          </div>

          {(data.top_campaigns || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-200/60">
              <p className="text-[9px] uppercase font-bold text-emerald-700/70 mb-1.5">
                Top contributors
              </p>
              <div className="space-y-1">
                {data.top_campaigns.slice(0, 3).map((c) => (
                  <div
                    key={c.campaign_id}
                    className="flex items-center justify-between text-[11px]"
                    data-testid={`top-campaign-${c.campaign_id}`}
                  >
                    <span className="truncate text-emerald-800 font-medium max-w-[60%]">{c.campaign_name}</span>
                    <span className="font-bold text-emerald-700">
                      ₹{Math.round(c.revenue_inr).toLocaleString("en-IN")} · {c.orders}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
